import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  QualifiedPerson,
  QualifiedPersonFormData,
  PbrApplication,
  PbrApplicationCreateData,
  PbrApplicationEvent,
  PbrCandidate,
  PbrDashboardSummary,
  PbrStage,
  ComplianceDocument,
} from '../types/cultivar';

interface GeneratedPdfResult {
  documentId: number;
  filePath: string;
  fileName: string;
  fileSizeBytes: number;
  downloadUrl: string;
}

export interface PbrLookupResult {
  applied: {
    protections: Array<{
      id: number;
      jurisdiction: string;
      type: string;
      status: string;
      registration_number: string | null;
      grant_date: string | null;
      expiry_date: string | null;
      authority: string | null;
      source_url: string | null;
    }>;
    cultivar_updates: Record<string, unknown>;
  };
  cultivar_after?: unknown;
  model?: string;
  web_searches_used?: number | null;
  raw?: string;
  warning?: string;
}

export interface IpgodSyncResult {
  source: 'ipgod';
  dataset: string;
  applied: {
    protections: Array<{
      id: number;
      jurisdiction: string;
      type: string;
      status: string;
      registration_number: string | null;
      grant_date: string | null;
      expiry_date: string | null;
      authority: string | null;
      source_url: string | null;
    }>;
    cultivar_updates: Record<string, unknown>;
  };
  cultivar_after?: unknown;
  candidates: Array<{
    application_number: string;
    denomination: string;
    applicant: string | null;
    status: string;
  }>;
  warning?: string;
}

class PbrService {
  // ---------------------------------------------------------------------------
  // Qualified Persons
  // ---------------------------------------------------------------------------

  async listQualifiedPersons(opts: { activeOnly?: boolean; species?: string } = {}): Promise<ApiResponse<QualifiedPerson[]>> {
    const qs = new URLSearchParams();
    if (opts.activeOnly) qs.set('active_only', 'true');
    if (opts.species) qs.set('species', opts.species);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiService.get(`/pbr/qualified-persons${suffix}`);
  }

  async getQualifiedPerson(id: number): Promise<ApiResponse<QualifiedPerson>> {
    return apiService.get(`/pbr/qualified-persons/${id}`);
  }

  async createQualifiedPerson(data: QualifiedPersonFormData): Promise<ApiResponse<QualifiedPerson>> {
    return apiService.post('/pbr/qualified-persons', data);
  }

  async updateQualifiedPerson(id: number, data: Partial<QualifiedPersonFormData>): Promise<ApiResponse<QualifiedPerson>> {
    return apiService.put(`/pbr/qualified-persons/${id}`, data);
  }

  async deleteQualifiedPerson(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiService.delete(`/pbr/qualified-persons/${id}`);
  }

  // ---------------------------------------------------------------------------
  // Candidates + dashboard
  // ---------------------------------------------------------------------------

  async listCandidates(): Promise<ApiResponse<PbrCandidate[]>> {
    return apiService.get('/pbr/candidates');
  }

  async getDashboard(): Promise<ApiResponse<PbrDashboardSummary>> {
    return apiService.get('/pbr/dashboard');
  }

  // ---------------------------------------------------------------------------
  // Applications
  // ---------------------------------------------------------------------------

  async listApplications(opts: { cultivarId?: number; stage?: PbrStage; jurisdiction?: string } = {}): Promise<ApiResponse<PbrApplication[]>> {
    const qs = new URLSearchParams();
    if (opts.cultivarId) qs.set('cultivar_id', String(opts.cultivarId));
    if (opts.stage) qs.set('stage', opts.stage);
    if (opts.jurisdiction) qs.set('jurisdiction', opts.jurisdiction);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return apiService.get(`/pbr/applications${suffix}`);
  }

  async getApplication(id: number): Promise<ApiResponse<PbrApplication>> {
    return apiService.get(`/pbr/applications/${id}`);
  }

  async createApplication(data: PbrApplicationCreateData): Promise<ApiResponse<PbrApplication>> {
    return apiService.post('/pbr/applications', data);
  }

  async updateApplication(id: number, data: Partial<PbrApplication>): Promise<ApiResponse<PbrApplication>> {
    return apiService.put(`/pbr/applications/${id}`, data);
  }

  async deleteApplication(id: number): Promise<ApiResponse<{ success: boolean }>> {
    return apiService.delete(`/pbr/applications/${id}`);
  }

  async transitionStage(id: number, newStage: PbrStage, note?: string): Promise<ApiResponse<PbrApplication>> {
    return apiService.post(`/pbr/applications/${id}/transition`, { new_stage: newStage, note });
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  async listEvents(id: number): Promise<ApiResponse<PbrApplicationEvent[]>> {
    return apiService.get(`/pbr/applications/${id}/events`);
  }

  async addEvent(id: number, event: { event_type: PbrApplicationEvent['event_type']; description?: string; event_date?: string }): Promise<ApiResponse<PbrApplicationEvent>> {
    return apiService.post(`/pbr/applications/${id}/events`, event);
  }

  // ---------------------------------------------------------------------------
  // Documents
  // ---------------------------------------------------------------------------

  async listDocuments(id: number): Promise<ApiResponse<ComplianceDocument[]>> {
    return apiService.get(`/pbr/applications/${id}/documents`);
  }

  async generatePart1(id: number): Promise<ApiResponse<GeneratedPdfResult>> {
    return apiService.post(`/pbr/applications/${id}/generate-part1`, {});
  }

  async generateNominationOfQp(id: number): Promise<ApiResponse<GeneratedPdfResult>> {
    return apiService.post(`/pbr/applications/${id}/generate-nomination-qp`, {});
  }

  async generateAuthorisationOfAgent(id: number): Promise<ApiResponse<GeneratedPdfResult>> {
    return apiService.post(`/pbr/applications/${id}/generate-authorisation-agent`, {});
  }

  // ---------------------------------------------------------------------------
  // Cultivar lookup (server-side, uses Anthropic API + web search)
  // ---------------------------------------------------------------------------

  async runCultivarLookup(cultivarId: number): Promise<ApiResponse<PbrLookupResult>> {
    return apiService.post(`/pbr/cultivars/${cultivarId}/lookup`, {});
  }

  // ---------------------------------------------------------------------------
  // IP Australia PBR registry sync (data.gov.au IPGOD — free, AU-only, no AI)
  // ---------------------------------------------------------------------------

  async runIpgodSync(cultivarId: number): Promise<ApiResponse<IpgodSyncResult>> {
    return apiService.post(`/pbr/cultivars/${cultivarId}/ipgod-sync`, {});
  }
}

export const pbrService = new PbrService();
export default pbrService;
